import React, { useState, useEffect } from "react"
import { Modal as BootstrapModal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "src/components/ui/select"
import { Label } from "src/components/ui/label"
import UserAdminService from "@admin/users/UserAdminService"

interface UserType {
  id: string
  name: string
}

interface ZeefAgentSetupModalProps {
  onConfirm: (userTypeId: string) => Promise<void>
}

const ZeefAgentSetupModal = NiceModal.create(({ onConfirm }: ZeefAgentSetupModalProps) => {
  const modal = useModal()
  const [userTypes, setUserTypes] = useState<UserType[]>([])
  const [selectedUserType, setSelectedUserType] = useState<string>("")
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchUserTypes() {
      try {
        const response = (await UserAdminService.getUserTypes()) as { userTypes: UserType[] }
        setUserTypes(response.userTypes)
      } catch (err) {
        setError("Failed to load user types")
      } finally {
        setIsLoading(false)
      }
    }
    fetchUserTypes()
  }, [])

  const handleConfirm = async () => {
    if (!selectedUserType) return
    setIsCreating(true)
    setError(null)
    try {
      await onConfirm(selectedUserType)
      modal.hide()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create agent")
      setIsCreating(false)
    }
  }

  return (
    <BootstrapModal centered size="md" isOpen={modal.visible} toggle={() => !isCreating && modal.hide()}>
      <ModalHeader>Configure ZEEF AI Agent</ModalHeader>
      <ModalBody>
        <p>The ZEEF AI assistant needs to be configured for your organization.</p>
        <p>Please select a user type for the AI agent:</p>

        {isLoading ? (
          <p>Loading user types...</p>
        ) : error ? (
          <p className="text-danger">{error}</p>
        ) : (
          <div className="mt-3">
            <Label htmlFor="userType">User Type</Label>
            <Select onValueChange={setSelectedUserType} value={selectedUserType}>
              <SelectTrigger id="userType">
                <SelectValue placeholder="Select a user type" />
              </SelectTrigger>
              <SelectContent style={{ zIndex: 9999 }}>
                {userTypes.map((userType) => (
                  <SelectItem key={userType.id} value={userType.id}>
                    {userType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => modal.hide()} disabled={isCreating}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={!selectedUserType || isCreating || isLoading}>
          {isCreating ? "Creating..." : "Create Agent"}
        </Button>
      </ModalFooter>
    </BootstrapModal>
  )
})

export default ZeefAgentSetupModal
